import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { FamilyTree, renderFamilyTree } from './FamilyTree';

/**
 * React component wrapper for D3 Family Tree chart (Plain/Hierarchical Layout)
 * 
 * @param {Object} props
 * @param {Object} props.data - Hierarchical data structure with family members
 * @param {Function} props.label - Function to get label from node data
 * @param {Function} props.title - Function to get title/hover text from node
 * @param {Function} props.onNodeClick - Callback when node is clicked
 * @param {number} props.width - Chart width
 * @param {number} props.height - Chart height
 * @param {number} props.margin - Chart margin
 * @param {string} props.selectedPersonId - ID of the selected person (highlighted)
 * @param {string} props.textColor - Text color for labels
 * @param {string} props.selectedTextColor - Text color for selected person
 */
const FamilyTreeComponent = ({
  data,
  label = (d) => d.name || d.first_name || 'Unknown',
  title = (d) => `${d.first_name || ''} ${d.last_name || ''}`.trim() || 'Unknown',
  onNodeClick = null,
  width = 1000,
  height = 800,
  margin = 100,
  selectedPersonId = null,
  textColor = "#333",
  selectedTextColor = "#F59E0B"
}) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || !data) {
      return;
    }

    // Calculate responsive size based on container
    // Use container dimensions for dynamic sizing
    const container = containerRef.current;
    const containerWidth = container.clientWidth || container.offsetWidth || width || 1000;
    const containerHeight = container.clientHeight || container.offsetHeight || height || 800;

    // Render the tree - it will calculate size from tree data and fit to container
    const svgElement = renderFamilyTree(data, {
      container: containerRef.current,
      label,
      title,
      onNodeClick,
      width: containerWidth,
      height: containerHeight,
      margin,
      selectedPersonId,
      textColor,
      selectedTextColor
    });

    // Auto-fit to window after rendering is complete
    // Use multiple attempts to ensure DOM is ready
    if (svgElement && svgElement.fitToWindow) {
      const attemptFit = () => {
        try {
          svgElement.fitToWindow();
        } catch (e) {
          console.error('Error fitting tree to window:', e);
        }
      };
      
      // Try immediately
      setTimeout(attemptFit, 50);
      // Try again after a short delay
      setTimeout(attemptFit, 200);
      // Final attempt after everything is rendered
      setTimeout(attemptFit, 500);
    }
  }, [data, label, title, onNodeClick, width, height, margin, selectedPersonId, textColor, selectedTextColor]);

  // Handle window resize to auto-fit the tree
  useEffect(() => {
    if (!containerRef.current || !data) {
      return;
    }

    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (containerRef.current) {
          // Find the SVG element in the container
          const svgElement = containerRef.current.querySelector('svg');
          if (svgElement && svgElement.fitToWindow) {
            // Update SVG dimensions based on new container size
            const container = containerRef.current;
            const containerWidth = container.clientWidth || container.offsetWidth || width || 1000;
            const containerHeight = container.clientHeight || container.offsetHeight || height || 800;
            
            // Update SVG viewBox and dimensions to match container
            d3.select(svgElement)
              .attr("width", containerWidth)
              .attr("height", containerHeight)
              .attr("viewBox", [0, 0, containerWidth, containerHeight].join(' '));
            
            // Auto-fit to the new size after a brief delay for DOM to update
            setTimeout(() => {
              if (svgElement.fitToWindow) {
                svgElement.fitToWindow();
              }
            }, 100);
          }
        }
      }, 250); // Debounce resize events
    };

    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
    };
  }, [data, width, height]);

  return (
    <div 
      ref={containerRef}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    />
  );
};

export default FamilyTreeComponent;
